/* eslint-disable no-underscore-dangle */
import React, { useState } from 'react';
import { Text, Box, Card, Avatar, Button } from 'grommet';
import { Facebook } from 'grommet-icons';
import Toast from '../../components/Toast';
import ApiErrorDisplay from '../../helpers/ApiErrorDisplay';
import {
  FacebookChannel,
  useFacebookScopes,
  useFacebookSettings,
} from '../../helpers/facebook';
import { FacebookSettingsModalV2 } from '@helpers/FacebookSettingsModalV2';
import { LiveStreamSettingsModal } from '@helpers/LiveStreamSettingsModal';
import FacebookDestinationPickerV3 from './FacebookDestinationPickerV3';
import HowToAndErrorModal from '@helpers/HowToAndErrorStreamingModal';
import { FACEBOOK_HOW_TO_SET_UP_STREAMING } from '@constants/streamingHelpTexts';
import { analytics } from '@services/analytics';
import { dtos } from '@shd/jslib/models';

interface Props {
  teamId: string | undefined;
  onComplete?: () => void;
  onUpdatedLinkedChannel?: (channel: FacebookChannel | null) => void;
}

function mapPublishFbToFacebookChannel(
  publishFb: dtos.PublishFb
): FacebookChannel {
  return {
    id: publishFb.destNodeId || publishFb._id,
    name: publishFb.destNodeName || publishFb.humanPublishName,
    label: publishFb.humanPublishName,
    type: publishFb.destNodeType || 'unknown',
    picture: publishFb.destNodeUrl
      ? {
          data: {
            height: 100,
            is_silhouette: false,
            url: publishFb.destNodeUrl,
            width: 100,
          },
        }
      : undefined,
  };
}

const FacebookSettings: React.FC<Props> = ({
  teamId,
  onComplete = () => {},
  onUpdatedLinkedChannel = () => {},
}) => {
  const {
    linkedChannel,
    onClose,
    onSave,
    linkingLoading,
    error,
    setError,
    onUnlinkFacebookClick,
    setShowIntroModal,
    showExpiration,
    onLinkFacebookClick,
    setShowLinkingModal,
    responseFacebook,
    showIntroModal,
    showLinkingModal,
    linkingError,
  } = useFacebookSettings(teamId, onComplete, onUpdatedLinkedChannel);
  const fbScopes = useFacebookScopes();
  const [showHowToSetUpModal, setShowHowToSetUpModal] =
    useState<boolean>(false);
  const [stepDestinationTitle, setStepDestinationTitle] = useState<string>(
    'Where do you want to stream to?'
  );
  const [destinationGoBack, setDestinationGoBack] = useState<() => void>();
  const [fbChannel, setFbChannel] = useState<FacebookChannel>({
    id: '',
    name: '',
    label: '',
    type: '',
    picture: undefined,
  });

  const emptyLinkedChannelState = (
    <Box direction="row" align="center" gap="small" pad="small">
      <Avatar background="brand">XY</Avatar>
      <Text>Link your profile, a group, or a page from below.</Text>
    </Box>
  );

  const linkedChannelState = linkedChannel ? (
    <Box direction="row" align="center" gap="small" pad="small">
      {linkedChannel.picture ? (
        <Avatar src={linkedChannel.picture.data.url} />
      ) : (
        <Avatar background="brand">
          {linkedChannel.name ? linkedChannel.name.substring(0, 2) : 'XY'}
        </Avatar>
      )}
      <Text>{`${linkedChannel.name} (${linkedChannel.type})`}</Text>
    </Box>
  ) : null;

  const signedInDiv = (
    <Card background="white" pad="small" skeleton={linkingLoading}>
      <Box gap="small" width="large">
        {linkedChannel ? (
          <Box margin="small" justify="center">
            <Text weight="bold">Streaming to: </Text>
            {linkedChannel ? linkedChannelState : emptyLinkedChannelState}
          </Box>
        ) : null}
        {error ? (
          <Toast
            label={error.toString()}
            background="status-error"
            duration={3000}
            onClose={() => {
              setError(null);
            }}
          />
        ) : null}
        <Box width="medium">
          <Button
            fill="horizontal"
            secondary
            color="status-error"
            icon={<Facebook color="plain" />}
            label="Unlink Facebook"
            onClick={onUnlinkFacebookClick}
          />
        </Box>
      </Box>
    </Card>
  );

  const signedOutDiv = (
    <Box
      direction="row"
      align="center"
      gap="small"
      fill="horizontal"
      skeleton={linkingLoading}
    >
      <Box width="medium">
        <Button
          fill="horizontal"
          primary
          color="white"
          icon={<Facebook color="plain" />}
          label="Link Facebook"
          onClick={() => {
            setShowIntroModal(true);
          }}
        />
      </Box>
    </Box>
  );

  const expirationPrompt = (
    <Box margin={{ top: 'small' }}>
      {(showExpiration.daysUntilExpiration ?? 0) < 0 ? (
        <Text color="status-critical">
          Facebook has expired your publishing link. To refresh, click UNLINK,
          then relink your destination.
        </Text>
      ) : (
        <Box>
          <Text>
            {`FB periodically expires publishing access.  This link will expire in ${showExpiration.daysUntilExpiration} days.`}
            To refresh, click UNLINK, then relink your destination.
          </Text>
        </Box>
      )}
    </Box>
  );

  const onCloseIntroModal = () => {
    setShowIntroModal(false);
  };

  return (
    <Box>
      {linkedChannel ? signedInDiv : signedOutDiv}
      {showExpiration.daysUntilExpiration && expirationPrompt}
      {showIntroModal && (
        <FacebookSettingsModalV2
          onLinkFacebookClick={onLinkFacebookClick}
          onCloseIntroModal={onCloseIntroModal}
          setShowLinkingModal={setShowLinkingModal}
          setShowHowToSetUpModal={() => {
            setShowHowToSetUpModal(true),
              analytics.track('Destination Path Choice Selected', {
                pathSelected: 'No',
                destinationType: 'Facebook',
                teamId,
              });
          }}
          fbScopes={fbScopes}
          responseFacebook={responseFacebook}
          teamId={teamId}
        />
      )}
      {showHowToSetUpModal && (
        <HowToAndErrorModal
          headerText="LINK FACEBOOK"
          title="How to set up Facebook livestreaming"
          subtitle=""
          onClose={() => setShowHowToSetUpModal(false)}
          isError={false}
          htmlContent={FACEBOOK_HOW_TO_SET_UP_STREAMING}
        />
      )}
      {showLinkingModal && (
        <LiveStreamSettingsModal
          headerText="LINK FACEBOOK"
          title={stepDestinationTitle}
          onCloseModal={onClose}
          goBack={destinationGoBack}
        >
          <FacebookDestinationPickerV3
            setStepDestinationTitle={setStepDestinationTitle}
            setDestinationGoBack={setDestinationGoBack}
            teamId={teamId}
            onClose={() => {
              onClose();
              setShowIntroModal(false), onSave(fbChannel);
            }}
            onLinked={(p: dtos.PublishFb) => {
              const facebookChannel = mapPublishFbToFacebookChannel(p);
              setFbChannel(facebookChannel);
            }}
          />
        </LiveStreamSettingsModal>
      )}
      <ApiErrorDisplay
        error={linkingError}
        header={
          <Box gap="small">
            <Box direction="row" gap="small" align="center">
              <Facebook size="small" color="plain" />
              <Text weight="bold">Error via Facebook</Text>
            </Box>
          </Box>
        }
      />
    </Box>
  );
};

export default FacebookSettings;
